// Builds JSON-LD schema (Schema.org) for the Skills Hub page from the same
// `categories` array the page renders from. Keeping schema generation tied to
// the live data means the schema can never drift from what's visible. When a
// category is renamed, paused, or retired in Firebase, both the page and the
// schema update on the next ISR revalidation.

import { Category } from "../interfaces";

const SITE_URL = "https://sols.foundation";
const PROVIDER_NAME = "SOLS Foundation";

const isPublished = (c: Category) => c.lcCategoryStatus === "PUBLISHED";

const stripHtml = (html: string | undefined): string => {
  if (!html) return "";
  return html
    .replace(/<[^>]+>/g, "")
    .replace(/\s+/g, " ")
    .trim();
};

export const buildSkillsHubSchema = (categories: Category[] | undefined) => {
  const published = (categories || []).filter(isPublished);

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "SOLS Foundation Skills Hub: Free Online Courses",
    // Names the categories actually on offer, so the description cannot claim
    // subject areas that have nothing bookable in them.
    description: `Free online courses by ${PROVIDER_NAME}: ${published
      .map((category) => category.lcCategoryName)
      .join(", ")}. All courses are free and award a certificate on completion.`,
    url: `${SITE_URL}/skills-hub`,
    numberOfItems: published.length,
    itemListElement: published.map((category, index) => ({
      "@type": "ListItem",
      position: index + 1,
      // Each /skills-hub tile is a course *category* (a collection of courses),
      // not a course itself. Model it as a CollectionPage; the actual Course
      // entities live on the category page (see lib/categorySchema.ts).
      item: {
        "@type": "CollectionPage",
        name: category.lcCategoryName,
        description:
          stripHtml(category.lcCategoryDesc) ||
          stripHtml(category.lcCategoryHtml) ||
          `Free courses offered by ${PROVIDER_NAME}.`,
        url: `${SITE_URL}/skills-hub/${category.lcCategoryId}`,
        ...(category.lcCategoryImage ? { image: category.lcCategoryImage } : {}),
      },
    })),
  };
};
